import classnames from "classnames";
import {cloneElement, ReactElement} from "react";

import {thousandSeparatedInt} from "@/lib/format.tsx";
import {BillingPeriod} from "@/lib/types/billing.ts";
import {SubscriptionPackTierWrapped} from "@/pages/billing/v2/subscriptions/Subscriptions.tsx";
import {PrivacyFooter} from "@/pages/billing/v2/summary/components/PrivacyFooter.tsx";

import styles from "@/pages/billing/v2/summary/CostBreakdown.module.scss";

export interface CostBreakdownProps {
  title: string;
  variant?: "primary" | "success";
  billingPeriod: BillingPeriod;
  items: SubscriptionPackTierWrapped[];
  disabled?: boolean;
  children: ReactElement<{
    disabled: boolean;
  }>;
}

export const CostBreakdown = ({
  title,
  billingPeriod,
  items,
  disabled,
  children,
  variant = "primary",
}: CostBreakdownProps) => {
  const total = items
    .map((i) => (billingPeriod === BillingPeriod.MONTH ? i.price_monthly : i.price_annual * 12))
    .reduce((a, b) => a + b, 0);
  const totalName = billingPeriod == BillingPeriod.MONTH ? "Monthly" : "Yearly";
  const suffix = billingPeriod == BillingPeriod.MONTH ? "mo" : "yr";
  return (
    <div
      id="costBreakdownContainer"
      className={classnames(styles.costBreakdown, "rounded-3 p-5", {
        [styles.primary]: variant === "primary",
        [styles.success]: variant === "success",
        "shadow-sm": variant === "primary",
      })}
    >
      <div>
        <div>
          <h2 className="pulseTextH2Bold mb-4">{title}</h2>
        </div>
        {items.map((item, index) => (
          <div className="mt-3 mb-4" key={index}>
            <div className="d-flex align-items-center">
              <h3 className="pulseTextH3">{item.packTitle}</h3>
              <span className="ms-auto">
                $
                <span className="pulseTextH2Bold ms-1">
                  {thousandSeparatedInt(
                    billingPeriod == BillingPeriod.MONTH ? item.price_monthly : item.price_annual * 12
                  )}
                </span>{" "}
                / {suffix}
              </span>
            </div>
            <ul className="mt-2 list-unstyled mb-2 pulseTextBody">
              {item.limits.map((line, index) =>
                line.value ? (
                  <li className="pulseTextBodySmall mt-1" key={index}>
                    {line.value_alt || thousandSeparatedInt(line.value)} {line.limit_name}
                  </li>
                ) : (
                  false
                )
              )}
            </ul>
          </div>
        ))}
      </div>
      <div>
        <hr className="mt-4" />
        <div className="d-flex align-items-center pulseTextH3Bold">
          <span>{variant === "success" ? "Total Due" : `${totalName} Total`}</span>
          <span className="ms-auto">
            $<span className="ms-1 pulseTextH2Bold">{thousandSeparatedInt(total)}</span> / {suffix}
          </span>
        </div>
        <div className="mt-3">{cloneElement(children, {disabled: disabled || total <= 0})}</div>
        <PrivacyFooter />
      </div>
    </div>
  );
};
